import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output
} from "@angular/core";
import {
  PanelAlertEntry,
  PanelCommandKey,
  PanelFeeder,
  PanelFeederStatus
} from "../../../interfaces/panel-alerts.interface";
import {
  countFeederProgress,
  feederStatus
} from "../../../utils/panel-alerts.utils";
import { PANEL_POWER_COMMANDS } from "../../../constants/panel-alerts.constants";

/** Icono del badge de estado del equipo en la cabecera (compacto). */
const STATUS_ICON: { [status: string]: string } = {
  idle: "",
  answered: "fa-check",
  waiting: "fa-clock",
  with_error: "fa-times"
};

/**
 * Tarjeta de un equipo con el resultado de cada comando que se le envio.
 *
 * El contador de la cabecera solo suma los procesos terminados: un comando que
 * sigue esperando respuesta no cuenta.
 */
@Component({
  selector: "app-panel-feeder-card",
  templateUrl: "./feeder-card.html",
  styleUrls: ["../feeders.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PanelFeederCardComponent implements OnChanges {
  @Input() feeder: PanelFeeder;
  @Input() sentCommands: PanelCommandKey[] = [];
  @Input() timezone = "";

  /** Se emite con el identificador del equipo al marcarlo o desmarcarlo. */
  @Output() toggleCheck = new EventEmitter<number>();
  /** Se emite para reenviar un comando a este equipo. */
  @Output() retry = new EventEmitter<{ feeder: PanelFeeder; alert: PanelAlertEntry }>();

  /** Procesos terminados de este equipo. */
  done = 0;
  /** Comandos que se le enviaron. */
  total = 0;
  /** Estado resumido del equipo, para el badge de la cabecera. */
  status: PanelFeederStatus = "idle";
  /** Icono del badge de estado; vacio si no se le envio nada. */
  statusIcon = "";
  /** En compacto, si la tarjeta muestra sus filas de comando. */
  expanded = false;
  /** Ultima alerta de potencia (turn_on o turn_off); null si no se envio ninguna. */
  powerAlert: PanelAlertEntry | null = null;

  /**
   * Recalcula cuantos procesos ha terminado el equipo.
   * @returns void
   */
  ngOnChanges(): void {
    if (!this.feeder) {
      return;
    }
    const progress = countFeederProgress(this.feeder, this.sentCommands);
    this.done = progress.done;
    this.total = progress.total;
    this.status = feederStatus(this.feeder, this.sentCommands);
    this.statusIcon = STATUS_ICON[this.status];

    /* Toma la ultima alerta de potencia enviada; si se mando primero turn_off
       y luego turn_on, se muestra solo el estado actual (turn_on). */
    const powerAlerts = this.feeder.alerts.filter(
      (a) => PANEL_POWER_COMMANDS.indexOf(a.alert) >= 0
    );
    this.powerAlert = powerAlerts.length > 0 ? powerAlerts[powerAlerts.length - 1] : null;
  }

  /**
   * Marca o desmarca este equipo sin expandir ni colapsar la tarjeta.
   * @param event Clic sobre la casilla.
   */
  onToggle(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.toggleCheck.emit(this.feeder.id);
  }

  /** Expande o colapsa las filas de comando (solo tiene efecto visual en compacto). */
  toggleExpanded(): void {
    this.expanded = !this.expanded;
  }

  /**
   * Reenvia al padre la peticion de reintento.
   * @param event Equipo y alerta a reintentar.
   */
  onRetry(event: { feeder: PanelFeeder; alert: PanelAlertEntry }): void {
    this.retry.emit(event);
  }

  /**
   * Identifica cada comando de la tarjeta para reutilizar sus filas.
   * @param index Posicion en la lista.
   * @param alert Entrada de historial de esa posicion.
   * @returns Clave estable del comando.
   */
  trackByAlert(index: number, alert: PanelAlertEntry): string {
    return alert.alert;
  }
}
